import { AUDIO_CONFIG } from './constants';

/**
 * Game audio system using Web Audio API
 * Generates procedural sound effects and background music
 */
export class GameAudio {
  private audioContext: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private bgmGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;

  private isMuted = false;
  private isInitialized = false;

  private bgmInterval: number | null = null;
  private bgmPlaying = false;

  constructor() {
    this.isMuted = localStorage.getItem('audioMuted') === 'true';
  }

  // ===========================================================================
  // Initialization & Control
  // ===========================================================================

  /**
   * Initialize audio context (must be called after user interaction)
   */
  public init(): void {
    if (this.isInitialized) return;

    try {
      this.audioContext = new AudioContext();

      this.masterGain = this.audioContext.createGain();
      this.masterGain.connect(this.audioContext.destination);
      this.masterGain.gain.value = this.isMuted ? 0 : 1;

      this.bgmGain = this.audioContext.createGain();
      this.bgmGain.connect(this.masterGain);
      this.bgmGain.gain.value = AUDIO_CONFIG.BGM_VOLUME;

      this.sfxGain = this.audioContext.createGain();
      this.sfxGain.connect(this.masterGain);
      this.sfxGain.gain.value = AUDIO_CONFIG.SFX_VOLUME;

      this.isInitialized = true;
    } catch {
      console.warn('Web Audio API not supported');
    }
  }

  public toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    localStorage.setItem('audioMuted', String(this.isMuted));

    if (this.masterGain && this.audioContext) {
      this.masterGain.gain.setValueAtTime(
        this.isMuted ? 0 : 1,
        this.audioContext.currentTime
      );
    }

    return this.isMuted;
  }

  public getMuted(): boolean {
    return this.isMuted;
  }

  public resume(): void {
    if (this.audioContext?.state === 'suspended') {
      this.audioContext.resume();
    }
  }

  // ===========================================================================
  // Sound Effects
  // ===========================================================================

  public playJump(): void {
    if (!this.canPlay()) return;

    const now = this.audioContext!.currentTime;
    const osc = this.createOscillator('sine');
    const gain = this.createGain();

    osc.connect(gain);
    gain.connect(this.sfxGain!);

    osc.frequency.setValueAtTime(300, now);
    osc.frequency.exponentialRampToValueAtTime(600, now + 0.1);

    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);

    osc.start(now);
    osc.stop(now + 0.15);
  }

  public playCrash(): void {
    if (!this.canPlay()) return;

    const now = this.audioContext!.currentTime;
    const bufferSize = this.audioContext!.sampleRate * 0.3;
    const buffer = this.audioContext!.createBuffer(1, bufferSize, this.audioContext!.sampleRate);
    const data = buffer.getChannelData(0);

    // Generate noise with decay
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.1));
    }

    const noise = this.audioContext!.createBufferSource();
    noise.buffer = buffer;

    const filter = this.audioContext!.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(500, now);
    filter.frequency.exponentialRampToValueAtTime(100, now + 0.3);

    const gain = this.createGain();
    gain.gain.setValueAtTime(0.5, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.3);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.sfxGain!);

    noise.start(now);
  }

  public playMilestone(): void {
    if (!this.canPlay()) return;

    const now = this.audioContext!.currentTime;
    const notes = [523.25, 659.25, 783.99]; // C5, E5, G5

    notes.forEach((freq, i) => {
      const osc = this.createOscillator('triangle');
      const gain = this.createGain();

      osc.connect(gain);
      gain.connect(this.sfxGain!);

      osc.frequency.value = freq;

      const startTime = now + i * 0.08;
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.2, startTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.2);

      osc.start(startTime);
      osc.stop(startTime + 0.2);
    });
  }

  public playGameOver(): void {
    if (!this.canPlay()) return;

    const now = this.audioContext!.currentTime;
    const notes = [392, 349.23, 329.63, 261.63]; // G4, F4, E4, C4

    // Descending melody
    notes.forEach((freq, i) => {
      const osc = this.createOscillator('sawtooth');
      const gain = this.createGain();

      osc.connect(gain);
      gain.connect(this.sfxGain!);

      osc.frequency.value = freq;

      const startTime = now + 0.3 + i * 0.2;
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.25, startTime + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.35);

      osc.start(startTime);
      osc.stop(startTime + 0.4);
    });

    // Final bass note
    this.playBassDrone(now + 1.1);
  }

  private playBassDrone(startTime: number): void {
    const bassOsc = this.createOscillator('sine');
    const bassGain = this.createGain();
    const bassFilter = this.audioContext!.createBiquadFilter();

    bassOsc.frequency.value = 65.41; // C2
    bassFilter.type = 'lowpass';
    bassFilter.frequency.value = 200;

    bassGain.gain.setValueAtTime(0, startTime);
    bassGain.gain.linearRampToValueAtTime(0.4, startTime + 0.05);
    bassGain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.8);

    bassOsc.connect(bassFilter);
    bassFilter.connect(bassGain);
    bassGain.connect(this.sfxGain!);

    bassOsc.start(startTime);
    bassOsc.stop(startTime + 0.8);
  }

  // ===========================================================================
  // Background Music
  // ===========================================================================

  public startBGM(): void {
    if (!this.canPlay() || this.bgmPlaying) return;

    this.bgmPlaying = true;
    const bassNotes = [110, 110, 146.83, 130.81]; // A2, A2, D3, C3
    let noteIndex = 0;

    const playBeat = () => {
      if (!this.bgmPlaying || !this.canPlay()) return;

      const now = this.audioContext!.currentTime;

      // Bass note
      this.playBassNote(bassNotes[noteIndex % bassNotes.length], now);

      // Hi-hat on even beats
      if (noteIndex % 2 === 0) {
        this.playHiHat(now);
      }

      noteIndex++;
    };

    playBeat();
    this.bgmInterval = window.setInterval(playBeat, AUDIO_CONFIG.BGM_TEMPO_MS);
  }

  public stopBGM(): void {
    this.bgmPlaying = false;

    if (this.bgmInterval !== null) {
      clearInterval(this.bgmInterval);
      this.bgmInterval = null;
    }
  }

  private playBassNote(frequency: number, time: number): void {
    const osc = this.createOscillator('sawtooth');
    const gain = this.createGain();
    const filter = this.audioContext!.createBiquadFilter();

    osc.frequency.value = frequency;
    filter.type = 'lowpass';
    filter.frequency.value = 300;

    gain.gain.setValueAtTime(0.15, time);
    gain.gain.exponentialRampToValueAtTime(0.01, time + 0.3);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.bgmGain!);

    osc.start(time);
    osc.stop(time + 0.35);
  }

  private playHiHat(time: number): void {
    const buffer = this.audioContext!.createBuffer(1, 2000, this.audioContext!.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < 2000; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / 500);
    }

    const source = this.audioContext!.createBufferSource();
    source.buffer = buffer;

    const filter = this.audioContext!.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 8000;

    const gain = this.createGain();
    gain.gain.value = 0.08;

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.bgmGain!);

    source.start(time);
  }

  // ===========================================================================
  // Helpers
  // ===========================================================================

  private canPlay(): boolean {
    return this.isInitialized && this.audioContext !== null && this.sfxGain !== null;
  }

  private createOscillator(type: OscillatorType): OscillatorNode {
    const osc = this.audioContext!.createOscillator();
    osc.type = type;
    return osc;
  }

  private createGain(): GainNode {
    return this.audioContext!.createGain();
  }
}
